"use client"

/**
 * Blueprint tab — assessment-wide coverage + BandScales (same graphs as the
 * question preview sheet). Coverage is the whole paper, not only the section
 * currently being edited.
 */

import * as React from "react"

import { BankEmptyState } from "@/components/assessment-structure/bank-empty-state"
import { DIFFICULTY_SCALE, D_INDEX_SCALE, PBI_SCALE, USAGE_SCALE, percent } from "@/components/assessment-structure/band-scale-shared"
import { BandScale, CoverageStatusScale, DISCRIMINATION_SCALE_MAX, USAGE_SCALE_MAX, type CoverageStatusKey, type CoverageStatusSegment } from "@/components/assessment-structure/band-scale"
import { DEFAULT_EXAM_CATEGORIES } from "@/lib/exam-categories/defaults"
import { findCategory, getRootCategoryId } from "@/lib/exam-categories/tree"
import { labelForContentId, skillLabelForContentId } from "@/lib/blueprint/mapping"
import type { AssessmentBlueprintGap } from "@/lib/mock/assessment-add-questions"
import type { AssessmentBankQuestion } from "@/lib/mock/assessment-add-questions"
import {
  type MixBand,
  type MixFacet,
} from "@/lib/section-mix"
import {
  sectionDifficultyReading,
  sectionDiscriminationReading,
  sectionUsageReading,
} from "@/lib/section-performance"
import {
  DISCRIMINATION_METHODS,
  type DiscriminationMethodId,
} from "@/lib/discrimination"
import { StatusBadge } from "@exxatdesignux/ui/components/status-badge"
import type { StatusBadgeTone } from "@exxatdesignux/ui/components/status-badge"
import { Button } from "@exxatdesignux/ui/components/button"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@exxatdesignux/ui/components/collapsible"
import { Tip } from "@exxatdesignux/ui/components/tip"
import { cn } from "@/lib/utils"
import { type BlueprintCoverageState, type BlueprintGapProgress, rollupState, type SkillNode, type CategoryNode, type BloomNode, type LeafNode, groupCoverage } from "@/components/assessment-structure/blueprint-panel-shared"

export type BlueprintTargetIntent = "fill" | "avoid"

export interface BlueprintPanelProps {
  gapProgress: BlueprintGapProgress[]
  rows: AssessmentBankQuestion[]
  discriminationMethodId: DiscriminationMethodId
  onDiscriminationMethodChange: (id: DiscriminationMethodId) => void
  onFocusTarget: (
    entry: BlueprintGapProgress,
    intent: BlueprintTargetIntent
  ) => void
  onSelectMixBand: (band: MixBand) => void
  activeMixFacetScope: string | null
}

function stateLabel(state: BlueprintCoverageState): string {
  if (state === "over") return "Over covered"
  if (state === "met") return "Covered"
  return "Not covered"
}

function stateTone(state: BlueprintCoverageState): StatusBadgeTone {
  if (state === "over") return "warning"
  if (state === "met") return "success"
  return "danger"
}

function stateIcon(state: BlueprintCoverageState): string {
  if (state === "over") return "fa-circle-up"
  if (state === "met") return "fa-circle-check"
  return "fa-circle-xmark"
}

function meterFillClass(state: BlueprintCoverageState): string {
  if (state === "over") return "bg-[var(--chip-4)]"
  if (state === "met") return "bg-brand-1"
  return "bg-brand-1/62"
}

function filterTopics(
  topics: CategoryNode[],
  focus: CoverageStatusKey | null
): CategoryNode[] {
  if (!focus) return topics
  return topics
    .map((topic) => {
      const skills = topic.skills
        .map((skill) => {
          const blooms = skill.blooms
            .map((bloom) => ({
              ...bloom,
              leaves: bloom.leaves.filter((leaf) => leaf.state === focus),
            }))
            .filter((bloom) => bloom.leaves.length > 0)
          if (blooms.length === 0) return null
          return {
            ...skill,
            blooms,
            state: rollupState(blooms.map((b) => b.state)),
          }
        })
        .filter((skill): skill is SkillNode => skill != null)
      if (skills.length === 0) return null
      return {
        ...topic,
        skills,
        state: rollupState(skills.map((skill) => skill.state)),
      }
    })
    .filter((t): t is CategoryNode => t != null)
}

function stateShortLabel(state: BlueprintCoverageState): string {
  if (state === "over") return "Over"
  if (state === "met") return "Met"
  return "Open"
}

function CoverageMeter({
  filled,
  target,
  state,
  label,
}: {
  filled: number
  target: number
  state: BlueprintCoverageState
  label: string
}) {
  const pct =
    target === 0
      ? 0
      : Math.min(100, Math.round((Math.min(filled, target) / target) * 100))

  return (
    <div
      role="progressbar"
      aria-valuenow={state === "over" ? 100 : pct}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`${label}: ${filled} of ${target}, ${stateLabel(state)}`}
      className="relative h-1.5 w-full min-w-0 overflow-hidden rounded-full bg-secondary"
    >
      <div
        className={cn("h-full transition-[width]", meterFillClass(state))}
        style={{ width: `${state === "over" ? 100 : pct}%` }}
      />
    </div>
  )
}

function StatusChip({ state }: { state: BlueprintCoverageState }) {
  return (
    <StatusBadge
      size="sm"
      label={stateShortLabel(state)}
      tone={stateTone(state)}
      icon={stateIcon(state)}
    />
  )
}

function TargetRow({
  leaf,
  onFocus,
}: {
  leaf: LeafNode
  onFocus: (entry: BlueprintGapProgress, intent: BlueprintTargetIntent) => void
}) {
  const actionable = leaf.state === "under" || leaf.state === "over"
  const intent: BlueprintTargetIntent =
    leaf.state === "over" ? "avoid" : "fill"
  const tip =
    leaf.state === "over"
      ? "Open the bank on open gaps — add less of this target"
      : "Open the bank filtered to questions that fill this target"

  const body = (
    <div className="flex w-full min-w-0 flex-col gap-1 py-1.5">
      <div className="flex w-full min-w-0 items-start justify-between gap-2">
        <span className="min-w-0 flex-1 whitespace-normal break-words text-sm leading-5 text-foreground">
          {leaf.label}
        </span>
        <span className="flex shrink-0 items-center gap-1.5">
          <span className="font-mono text-xs tabular-nums text-muted-foreground">
            {leaf.filled}/{leaf.target}
          </span>
          <StatusChip state={leaf.state} />
        </span>
      </div>
      <CoverageMeter
        filled={leaf.filled}
        target={leaf.target}
        state={leaf.state}
        label={leaf.label}
      />
    </div>
  )

  if (!actionable) {
    return <li className="w-full min-w-0">{body}</li>
  }

  return (
    <li className="w-full min-w-0">
      <Tip label={tip}>
        <button
          type="button"
          onClick={() => onFocus(leaf.entry, intent)}
          className={cn(
            "w-full min-w-0 rounded-1 text-start",
            "hover:bg-muted/50",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          )}
          aria-label={`${leaf.label} (${leaf.entry.gap.bloom}): ${stateLabel(leaf.state)}. ${tip}.`}
        >
          {body}
        </button>
      </Tip>
    </li>
  )
}

function BloomBlock({
  node,
  onFocus,
}: {
  node: BloomNode
  onFocus: (entry: BlueprintGapProgress, intent: BlueprintTargetIntent) => void
}) {
  return (
    <Collapsible defaultOpen={false} className="group/bloom w-full min-w-0">
      <CollapsibleTrigger
        className={cn(
          "flex w-full min-w-0 items-start gap-2 rounded-1 py-1.5 text-start",
          "hover:bg-muted/40",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        )}
      >
        <i
          className="fa-light fa-chevron-right mt-1 shrink-0 text-xs text-muted-foreground transition-transform group-data-[state=open]/bloom:rotate-90"
          aria-hidden
        />
        <span className="min-w-0 flex-1 whitespace-normal break-words text-sm font-medium leading-5">
          {node.bloom}
        </span>
        <span className="flex shrink-0 items-center gap-1.5 pt-0.5">
          <span className="font-mono text-xs tabular-nums text-muted-foreground">
            {node.filled}/{node.target}
          </span>
          {node.state !== "met" ? <StatusChip state={node.state} /> : null}
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent className="w-full min-w-0 overflow-hidden">
        <div className="ms-2 w-full min-w-0 border-s border-border/60 ps-3">
          <ul className="flex w-full min-w-0 flex-col">
            {node.leaves.map((leaf) => (
              <TargetRow
                key={leaf.entry.scopeValue}
                leaf={leaf}
                onFocus={onFocus}
              />
            ))}
          </ul>
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

function SkillBlock({
  node,
  onFocus,
}: {
  node: SkillNode
  onFocus: (entry: BlueprintGapProgress, intent: BlueprintTargetIntent) => void
}) {
  return (
    <Collapsible defaultOpen={false} className="group/skill w-full min-w-0">
      <CollapsibleTrigger
        className={cn(
          "flex w-full min-w-0 items-start gap-2 rounded-1 py-1.5 text-start",
          "hover:bg-muted/40",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        )}
      >
        <i
          className="fa-light fa-chevron-right mt-1 shrink-0 text-xs text-muted-foreground transition-transform group-data-[state=open]/skill:rotate-90"
          aria-hidden
        />
        <span className="min-w-0 flex-1 whitespace-normal break-words text-sm font-medium leading-5">
          {node.label}
        </span>
        <span className="flex shrink-0 items-center gap-1.5 pt-0.5">
          <span className="font-mono text-xs tabular-nums text-muted-foreground">
            {node.filled}/{node.target}
          </span>
          {node.state !== "met" ? <StatusChip state={node.state} /> : null}
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent className="w-full min-w-0 overflow-hidden">
        <div className="ms-2 w-full min-w-0 border-s border-border/60 ps-3">
          {node.blooms.map((bloom) => (
            <BloomBlock key={bloom.bloom} node={bloom} onFocus={onFocus} />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

function TopicBlock({
  node,
  onFocus,
}: {
  node: CategoryNode
  onFocus: (entry: BlueprintGapProgress, intent: BlueprintTargetIntent) => void
}) {
  return (
    <Collapsible defaultOpen={false} className="group/topic w-full min-w-0">
      <CollapsibleTrigger
        className={cn(
          "flex w-full min-w-0 items-start gap-2 rounded-1 py-1.5 text-start",
          "hover:bg-muted/40",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        )}
      >
        <i
          className="fa-light fa-chevron-right mt-1 shrink-0 text-xs text-muted-foreground transition-transform group-data-[state=open]/topic:rotate-90"
          aria-hidden
        />
        <span className="min-w-0 flex-1 whitespace-normal break-words text-sm font-semibold leading-5">
          {node.topic}
        </span>
        <span className="flex shrink-0 items-center gap-1.5 pt-0.5">
          <span className="font-mono text-xs tabular-nums text-muted-foreground">
            {node.filled}/{node.target}
          </span>
          <StatusChip state={node.state} />
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent className="w-full min-w-0 overflow-hidden">
        <div className="w-full min-w-0 space-y-1 pb-1">
          <CoverageMeter
            filled={node.filled}
            target={node.target}
            state={node.state}
            label={node.topic}
          />
          <div className="ms-2 w-full min-w-0 border-s border-border/60 ps-3">
            {node.skills.map((skill) => (
              <SkillBlock key={skill.categoryId} node={skill} onFocus={onFocus} />
            ))}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

function MethodSwitch({
  methodId,
  onChange,
}: {
  methodId: DiscriminationMethodId
  onChange: (id: DiscriminationMethodId) => void
}) {
  const current = DISCRIMINATION_METHODS.find((m) => m.id === methodId)!
  const other = DISCRIMINATION_METHODS.find((m) => m.id !== methodId)!
  return (
    <Tip label={`Switch to ${other.abbreviation}`}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 px-1.5 text-xs"
        onClick={() => onChange(other.id)}
        aria-label={`Discrimination method ${current.abbreviation}. Switch to ${other.abbreviation}.`}
      >
        {current.abbreviation}
      </Button>
    </Tip>
  )
}

function bandToMixFacet(
  kind: "difficulty" | "discrimination",
  bandLabel: string,
  methodId: DiscriminationMethodId
): MixFacet | null {
  if (kind === "difficulty") {
    if (bandLabel === "Hard" || bandLabel === "Medium" || bandLabel === "Easy") {
      return { dimension: "difficulty", value: bandLabel }
    }
    return null
  }
  const key =
    bandLabel.startsWith("Doesn't")
      ? "weak"
      : bandLabel.startsWith("Separates somewhat")
        ? "adequate"
        : bandLabel.startsWith("Separates well")
          ? "strong"
          : null
  if (!key) return null
  return { dimension: methodId, value: key }
}

export function BlueprintPanel({
  gapProgress,
  rows,
  discriminationMethodId,
  onDiscriminationMethodChange,
  onFocusTarget,
  onSelectMixBand,
  activeMixFacetScope: _activeMixFacetScope,
}: BlueprintPanelProps) {
  const [statusFocus, setStatusFocus] =
    React.useState<CoverageStatusKey | null>(null)

  const topics = React.useMemo(() => groupCoverage(gapProgress), [gapProgress])
  const visibleTopics = React.useMemo(
    () => filterTopics(topics, statusFocus),
    [topics, statusFocus]
  )

  const leaves = React.useMemo(
    () =>
      topics.flatMap((topic) =>
        topic.skills.flatMap((skill) =>
          skill.blooms.flatMap((bloom) => bloom.leaves)
        )
      ),
    [topics]
  )

  const coverageSegments = React.useMemo<CoverageStatusSegment[]>(
    () => [
      {
        key: "under",
        label: "Not covered",
        count: leaves.filter((l) => l.state === "under").length,
        tone: "danger",
      },
      {
        key: "met",
        label: "Covered",
        count: leaves.filter((l) => l.state === "met").length,
        tone: "success",
      },
      {
        key: "over",
        label: "Over covered",
        count: leaves.filter((l) => l.state === "over").length,
        tone: "warning",
      },
    ],
    [leaves]
  )

  const usage = React.useMemo(() => sectionUsageReading(rows), [rows])
  const difficulty = React.useMemo(
    () => sectionDifficultyReading(rows),
    [rows]
  )
  const discrimination = React.useMemo(
    () => sectionDiscriminationReading(rows, discriminationMethodId),
    [rows, discriminationMethodId]
  )

  if (gapProgress.length === 0) {
    return (
      <div className="p-3">
        <BankEmptyState
          density="panel"
          icon="fa-bullseye-arrow"
          title="No blueprint yet"
          description="Set up blueprint targets for this assessment to track covered, not covered, and over covered."
        />
      </div>
    )
  }

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col gap-4 overflow-y-auto overscroll-contain p-3">
      <CoverageStatusScale
        segments={coverageSegments}
        activeKey={statusFocus}
        onSelect={setStatusFocus}
      />

      <section className="flex w-full min-w-0 flex-col gap-2" aria-labelledby="bp-targets">
        <h2 id="bp-targets" className="text-sm font-medium text-foreground">
          Targets
          {statusFocus ? (
            <span className="ms-1.5 font-normal text-muted-foreground">
              · {stateLabel(statusFocus)}
            </span>
          ) : null}
        </h2>
        {visibleTopics.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No targets in this state.
          </p>
        ) : (
          <div className="flex w-full min-w-0 flex-col gap-3">
            {visibleTopics.map((topic) => (
              <TopicBlock
                key={topic.categoryId}
                node={topic}
                onFocus={onFocusTarget}
              />
            ))}
          </div>
        )}
      </section>

      <div className="flex flex-col divide-y divide-border border-t border-border/50 pt-2">
        <BandScale
          icon="fa-arrows-rotate"
          label="Usage"
          bands={USAGE_SCALE}
          max={USAGE_SCALE_MAX}
          value={
            usage.value == null
              ? null
              : Math.min(USAGE_SCALE_MAX, usage.value)
          }
          rangeLow={usage.rangeLow}
          rangeHigh={usage.rangeHigh}
          reading={usage.reading}
          aside={usage.aside}
          format={(v) => `${v}`}
        />
        <BandScale
          icon="fa-bullseye-arrow"
          label="Difficulty"
          bands={DIFFICULTY_SCALE}
          max={1}
          value={difficulty.value}
          rangeLow={difficulty.rangeLow}
          rangeHigh={difficulty.rangeHigh}
          reading={difficulty.reading}
          aside={difficulty.aside}
          format={percent}
          onBandSelect={(band) => {
            const facet = bandToMixFacet("difficulty", band.label, discriminationMethodId)
            if (!facet) return
            onSelectMixBand({
              key: band.label,
              label: band.label,
              count: 0,
              facet,
              glyph: { kind: "icon", icon: "fa-bullseye-arrow" },
              fillClassName: "bg-brand-1/15",
            })
          }}
        />
        <div className="relative">
          <div className="absolute end-0 top-3.5 z-10">
            <MethodSwitch
              methodId={discriminationMethodId}
              onChange={onDiscriminationMethodChange}
            />
          </div>
          <BandScale
            icon="fa-scale-balanced"
            label="Discrimination"
            bands={
              discriminationMethodId === "pbi" ? PBI_SCALE : D_INDEX_SCALE
            }
            max={DISCRIMINATION_SCALE_MAX}
            value={discrimination.value}
            rangeLow={discrimination.rangeLow}
            rangeHigh={discrimination.rangeHigh}
            reading={discrimination.reading}
            aside={discrimination.aside}
            format={(v) => v.toFixed(2)}
            onBandSelect={(band) => {
              const facet = bandToMixFacet(
                "discrimination",
                band.label,
                discriminationMethodId
              )
              if (!facet) return
              onSelectMixBand({
                key: `${facet.dimension}:${facet.value}`,
                label: band.label,
                count: 0,
                facet,
                glyph: { kind: "icon", icon: "fa-scale-balanced" },
                fillClassName: "bg-brand-1/15",
              })
            }}
          />
        </div>
      </div>
    </div>
  )
}
